import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = ["onSubmit"]
const _hoisted_2 = { class: "form__submit" }
const _hoisted_3 = {
  class: "btn btn--fullscreen",
  type: "submit"
}

import {useI18n} from "vue3-i18n";
import {computed, onMounted, ref} from "vue";
import FormRow from "@/components/forms/FormRow.vue";
import {Document, IAddDocumentsRequest} from "@/types/entities/document";
import {DocumentCategoryHelper, DocumentPolicy, Guid, Role} from "@/types";
import {DocumentPolicyHelper} from "@/types/documentPolicy";
import {useDocumentService} from "@/inversify.config";
import {notifyError, notifySuccess} from "@/notify";
import {useRouter} from "vue-router";
import {Status} from "@/validation";
import FormInput from "@/components/forms/FormInput.vue";
import FormRadios from "@/components/forms/FormRadios.vue";
import FormFiles from "@/components/forms/FormFiles.vue";
import FormSelect from "@/components/forms/FormSelect.vue";
import {SucceededOrNotResponse} from "@/types/responses";
import {useMemberStore} from "@/stores/memberStore";

// eslint-disable-next-line no-undef

export default /*#__PURE__*/_defineComponent({
  __name: 'DocumentForm',
  props: {
    id: {}
  },
  setup(__props: any) {

const props = __props

const {t} = useI18n();

const router = useRouter();
const documentService = useDocumentService();

const memberStore = useMemberStore();
const canSeeAllPolicies = memberStore.hasOneOfTheseRoles([Role.Admin, Role.CorporateAdmin, Role.RegularSyndicateAdmin, Role.RegularSyndicateManager])

const isEditMode = Guid.isValid(props.id);
const document = ref<Document>() //useful for the edit state
const files = ref<any>() //useful for the add state
const policy = ref<number | string>(0)
const category = ref<string>()

onMounted(async () => {
  if (!isEditMode) return

  const doc = await documentService.getDocument(props.id);

  document.value = doc;
  files.value = doc.document;
  policy.value = doc.policy ?? DocumentPolicy.Private;
  category.value = doc.category;
})

const documentPolicies = computed(() => {
  if (canSeeAllPolicies) return DocumentPolicyHelper.policiesValueAndNames()
  return [DocumentPolicyHelper.policiesValueAndNames()[0]]
});

function onPolicyChange(event: any) {
  if (isEditMode && document.value) document.value.policy = event.target.value;
}

const documentCategories = computed(() =>
    DocumentCategoryHelper.categoriesNameAndLabel().sort((a, b) => a.label > b.label ? 1 : -1)
);

function onCategoryChange(event: any) {
  if (isEditMode && document.value) document.value.category = event.target.value;
}

function onFileChange(event: any) {
  if (isEditMode && document.value) document.value.document = event.target.files[0];
}

//Validation -------
const formInputs = ref<(typeof FormInput)[]>([]);
const inputValidationStatuses: any = {};

function addFormInputRef(ref: typeof FormInput) {
  if (!formInputs.value.includes(ref)) formInputs.value.push(ref);
}

async function handleValidation(name: string, validationStatus: Status) {
  inputValidationStatuses[name] = validationStatus.valid;
}

//Submit ------------
async function handleSubmit() {
  formInputs.value.forEach((x: typeof FormInput) => x.validateInput())
  if (Object.values(inputValidationStatuses).some(x => x === false)) {
    notifyError(t('global.formErrorNotification'))
    return
  } else {
    await save();
  }
}

async function save() {
  let result: any;

  if (isEditMode) {
    result = await documentService.updateDocument(document.value as Document);
  } else {
    result = await documentService.addDocuments({
      policy: policy.value,
      category: category.value,
      // don't change Documents for documents, it makes model binding work in back-end
      Documents: files.value
    } as IAddDocumentsRequest);
  }

  if (result?.id || result[0]?.id) {
    notifySuccess(files.value?.length > 1
        ? t("document.saveMultipleSuccess").replace("{length}", files.value.length)
        : t("document.saveSuccess")
    );
    setTimeout(() => {
      router.back();
    }, 1500);
  } else {
    notifyError(files.value?.length > 1
        ? t("document.saveMultipleError")
        : t("document.saveError")
    );
  }
}

async function deleteDocument() {
  if (!Guid.isValid(props.id ?? "") || props.id == null) return;

  let confirmDelete = confirm(t("document.deleteConfirmation"));

  if (!confirmDelete) return;

  let result: SucceededOrNotResponse = await documentService.deleteDocument(props.id);
  if (result?.succeeded) {
    notifySuccess(t('document.deleteSuccess'))
    router.back();
  } else {
    notifyError(t('document.deleteError'))
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("form", {
    class: "form",
    enctype: "multipart/form-data",
    novalidate: "",
    onSubmit: _withModifiers(handleSubmit, ["prevent"])
  }, [
    _createVNode(FormRow, null, {
      default: _withCtx(() => [
        _createVNode(FormFiles, {
          ref: addFormInputRef,
          modelValue: files.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((files).value = $event)),
          "default-selected-files": document.value?.name != null ? [document.value.name] : [],
          "is-multiple": !_unref(isEditMode),
          label: _unref(t)('document.label'),
          min: 1,
          name: "document",
          onChange: onFileChange,
          onValidated: handleValidation
        }, null, 8, ["modelValue", "default-selected-files", "is-multiple", "label"])
      ]),
      _: 1
    }),
    _createVNode(FormRow, null, {
      default: _withCtx(() => [
        _createVNode(FormSelect, {
          ref: addFormInputRef,
          modelValue: category.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((category).value = $event)),
          label: _unref(t)('document.category'),
          options: documentCategories.value,
          name: "category",
          onChange: onCategoryChange,
          onValidated: handleValidation
        }, null, 8, ["modelValue", "label", "options"])
      ]),
      _: 1
    }),
    _createElementVNode("div", null, [
      _createVNode(FormRadios, {
        ref: addFormInputRef,
        modelValue: policy.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((policy).value = $event)),
        options: documentPolicies.value,
        title: _unref(t)('document.policy'),
        name: "radios",
        onChange: onPolicyChange,
        onValidated: handleValidation
      }, null, 8, ["modelValue", "options", "title"])
    ]),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("button", _hoisted_3, _toDisplayString(_unref(t)("global.save")), 1),
      (_unref(isEditMode))
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            class: "btn btn--red btn--fullscreen",
            type: "button",
            onClick: deleteDocument
          }, _toDisplayString(_unref(t)("global.delete")), 1))
        : _createCommentVNode("", true)
    ])
  ], 40, _hoisted_1))
}
}

})